import { apiRequest } from "../utils/useAPIRequest";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import constant from "../utils/constant";
import { queryClient } from "../App";

const useGetMoneyExLimit = (
  moneyExLimtTableQuery,
  filterQuery,
  searchQuery
) => {
  const query = useMemo(() => {
    if (searchQuery && filterQuery) {
      return `${moneyExLimtTableQuery}&${filterQuery}&search=${searchQuery}`;
    } else if (searchQuery) {
      return `${moneyExLimtTableQuery}&search=${searchQuery}`;
    } else if (filterQuery) {
      return `${moneyExLimtTableQuery}&${filterQuery}`;
    } else {
      return moneyExLimtTableQuery;
    }
  }, [filterQuery, moneyExLimtTableQuery, searchQuery]);

  return useQuery({
    queryKey: ["getMoneyExLimit", query],
    queryFn: () => {
      return apiRequest({
        url: `/p2p-money-ex-limit?${query}`,
        method: "get",
      });
    },
    keepPreviousData: true,
  });
};
const useGetMoneyExAds = (moneyExAdsTableQuery, filterQuery, searchQuery) => {
  const query = useMemo(() => {
    if (searchQuery && filterQuery) {
      return `${moneyExAdsTableQuery}&${filterQuery}&search=${searchQuery}`;
    } else if (searchQuery) {
      return `${moneyExAdsTableQuery}&search=${searchQuery}`;
    } else if (filterQuery) {
      return `${moneyExAdsTableQuery}&${filterQuery}`;
    } else {
      return moneyExAdsTableQuery;
    }
  }, [filterQuery, moneyExAdsTableQuery, searchQuery]);

  return useQuery({
    queryKey: ["getMoneyExAdds", query],
    queryFn: () => {
      return apiRequest({
        url: `/money-ex-ad?${query}`,
        method: "get",
      });
    },
    keepPreviousData: true,
  });
};
const useGetMoneyExUsers = (
  moneyExUsersTableQuery,
  filterQuery,
  searchQuery
) => {
  const query = useMemo(() => {
    if (searchQuery && filterQuery) {
      return `${moneyExUsersTableQuery}&${filterQuery}&search=${searchQuery}`;
    } else if (searchQuery) {
      return `${moneyExUsersTableQuery}&search=${searchQuery}`;
    } else if (filterQuery) {
      return `${moneyExUsersTableQuery}&${filterQuery}`;
    } else {
      return moneyExUsersTableQuery;
    }
  }, [filterQuery, moneyExUsersTableQuery, searchQuery]);

  return useQuery({
    queryKey: ["getMoneyExUsers", query],
    queryFn: () => {
      return apiRequest({
        url: `/money-ex-user/admin?${query}`,
        method: "get",
      });
    },
    keepPreviousData: true,
  });
};

const verifyInstantOrderPayment = async (payload) => {
  const { status, errorMessage, data } = await apiRequest({
    url: "/instant-money-ex-order/admin-buyer-payment-verification",
    method: "post",
    payload,
  });
  if (status === constant.Success) {
    queryClient.invalidateQueries({
      queryKey: ["getMoneyExInstantOrder"],
    });
    return { status: constant.Success, data };
  }
  return { errorMessage, status, data };
};
const verifyP2PPayment = async (payload) => {
  const { status, errorMessage, data } = await apiRequest({
    url: "/p2p-money-ex-order/admin-buyer-payment-verification",
    method: "post",
    payload,
  });
  if (status === constant.Success) {
    queryClient.invalidateQueries({
      queryKey: ["getP2POrders"],
    });
    return { status: constant.Success, data };
  }
  return { errorMessage, status, data };
};
const verifyP2PSellerPayment = async (payload) => {
  const { status, errorMessage, data } = await apiRequest({
    url: "/p2p-money-ex-order/admin-seller-payment-verification",
    method: "post",
    payload,
  });
  if (status === constant.Success) {
    queryClient.invalidateQueries({
      queryKey: ["getP2POrders"],
    });
    return { status: constant.Success, data };
  }
  return { errorMessage, status, data };
};

const updateInstantOrderPayment = async (payload) => {
  const { status, errorMessage, data } = await apiRequest({
    url: "/instant-money-ex-order/admin-buyer-payment-update",
    method: "post",
    payload,
  });
  if (status === constant.Success) {
    queryClient.invalidateQueries({
      queryKey: ["getMoneyExInstantOrder"],
    });
    return { status: constant.Success, data };
  }
  return { errorMessage, status, data };
};
const updateP2PPayment = async (payload) => {
  const { status, errorMessage, data } = await apiRequest({
    url: "/p2p-money-ex-order/admin-buyer-payment-update",
    method: "post",
    payload,
  });
  if (status === constant.Success) {
    queryClient.invalidateQueries({
      queryKey: ["getP2POrders"],
    });
    return { status: constant.Success, data };
  }
  return { errorMessage, status, data };
};
const updateP2PSellerPayment = async (payload) => {
  const { status, errorMessage, data } = await apiRequest({
    url: "/p2p-money-ex-order/admin-seller-payment-update",
    method: "post",
    payload,
  });
  if (status === constant.Success) {
    queryClient.invalidateQueries({
      queryKey: ["getP2POrders"],
    });
    return { status: constant.Success, data };
  }
  return { errorMessage, status, data };
};
const refundInstantOrderPayment = async (payload) => {
  const { status, errorMessage, data } = await apiRequest({
    url: "/instant-money-ex-order/admin-buyer-refund-payment",
    method: "post",
    payload,
  });
  if (status === constant.Success) {
    queryClient.invalidateQueries({
      queryKey: ["getMoneyExInstantOrder"],
    });
    return { status: constant.Success, data };
  }
  return { errorMessage, status, data };
};
const refundP2PPayment = async (payload) => {
  const { status, errorMessage, data } = await apiRequest({
    url: "/p2p-money-ex-order/admin-buyer-refund-payment",
    method: "post",
    payload,
  });
  if (status === constant.Success) {
    queryClient.invalidateQueries({
      queryKey: ["getP2POrders"],
    });
    return { status: constant.Success, data };
  }
  return { errorMessage, status, data };
};

const useGetP2POrders = (query) => {
  return useQuery({
    queryKey: ["getP2POrders", query],
    queryFn: () => {
      return apiRequest({
        url: `/p2p-money-ex-order?${query}`,
        method: "get",
      });
    },
    keepPreviousData: true,
  });
};
const useGetExpressOrders = (query) => {
  return useQuery({
    queryKey: ["getMoneyExInstantOrder", query],
    queryFn: () => {
      return apiRequest({
        url: `/instant-money-ex-order?${query}`,
        method: "get",
      });
    },
    keepPreviousData: true,
  });
};

const createLimit = async (payload) => {
  const { status, errorMessage, data } = await apiRequest({
    url: "/p2p-money-ex-limit",
    method: "post",
    payload,
  });
  if (status === constant.Success) {
    queryClient.invalidateQueries({
      queryKey: ["getMoneyExLimit"],
    });
    return { status: constant.Success, data };
  }
  return { errorMessage, status, data };
};
const updateLimit = async (payload, moneyExLimitId) => {
  const { status, errorMessage, data } = await apiRequest({
    url: `/p2p-money-ex-limit/moneyExLimit/${moneyExLimitId}`,
    method: "patch",
    payload,
  });
  if (status === constant.Success) {
    queryClient.invalidateQueries({
      queryKey: ["getMoneyExLimit"],
    });
    return { status: constant.Success, data };
  }
  return { errorMessage, status, data };
};

const createAd = async (payload) => {
  const { status, errorMessage, data } = await apiRequest({
    url: "/money-ex-ad/admin",
    method: "post",
    payload,
  });
  if (status === constant.Success) {
    queryClient.invalidateQueries({
      queryKey: ["getMoneyExAdds"],
    });
    return { status: constant.Success, data };
  }
  return { errorMessage, status, data };
};
const editAd = async (payload, moneyExAdId) => {
  const { status, errorMessage, data } = await apiRequest({
    url: `/money-ex-ad/admin/${moneyExAdId}`,
    method: "patch",
    payload,
  });
  if (status === constant.Success) {
    queryClient.invalidateQueries({
      queryKey: ["getMoneyExAdds"],
    });
    return { status: constant.Success, data };
  }
  return { errorMessage, status, data };
};

const MoneyExController = {
  useGetMoneyExAds,
  useGetMoneyExUsers,
  verifyInstantOrderPayment,
  updateInstantOrderPayment,
  refundInstantOrderPayment,
  verifyP2PPayment,
  updateP2PPayment,
  refundP2PPayment,
  useGetP2POrders,
  useGetExpressOrders,
  verifyP2PSellerPayment,
  updateP2PSellerPayment,
  useGetMoneyExLimit,
  createLimit,
  updateLimit,
  createAd,
  editAd,
};

export default MoneyExController;
